import { FC, useState } from 'react';
import { NudosGenericModal, NudosModalContent } from '../../../../components/NudosComponents';
import {
  editEstimatedDeliveryDateInBulk,
  massUpdateConfirmedToInProgress
} from '../../../../services/traking.services';
import {
  IGeneralData,
  IOption,
  IShipmentData,
  IproductReferenceDataInCountry,
  SHIPMENT_STATUS
} from '../../../../types/orders';
import { displayErrorNotification } from '../../../../utils/displayNudosStandardNotifications';
import { IEditEstimatedDeliveryDate } from '../../../../types/traking';
import { IWarehouseTemporaryStorageModalCustomSectionInitialValues } from '../../OrderDetailInternalCard/Components/Tab4Address/Components/ShipmentCard/WarehouseTemporaryStorageModal/WarehouseTemporaryStorageModalCustomSection';
import { updateStockOrderShipmentStatus, updateStockOrderShipmentTracking } from '../../../../services/stockOrders';
import DeliveryProcessModalContent from './DeliveryProcessModalContent';
import { extractProductData } from 'utils/extractProductData';
import './DeliveryProcessModal.scss';

export interface IFormDeliveryProcess {
  link?: string;
  code?: string;
  date?: string;
  stockShipmentUuid?: string;
}

interface IDeliveryProcessModal {
  listOfGroupedShipments: IShipmentData[];
  dataTraking: IShipmentData;
  changeShowModal?: () => void;
  updateDataTraking?: () => void;
  isEdited?: boolean;
  organizationName?: string;
  orderId?: number;
  receiverName?: string;
  flag?: string | null;
  generalData: IGeneralData;
  warehouseTemporaryStorage?: IWarehouseTemporaryStorageModalCustomSectionInitialValues;
  selectOption?: IOption;
  isStockOrder?: boolean;
  references?: IproductReferenceDataInCountry[];
}

const DeliveryProcessModal: FC<IDeliveryProcessModal> = ({
  references,
  changeShowModal,
  dataTraking,
  updateDataTraking,
  isEdited,
  organizationName = 'Nombre Empresa',
  receiverName = 'Nombre Cliente',
  orderId,
  flag = null,
  listOfGroupedShipments,
  generalData,
  warehouseTemporaryStorage,
  selectOption,
  isStockOrder = false
}: IDeliveryProcessModal) => {
  const [loadButton, setLoadButton] = useState<boolean>(false);

  const stateOfTheSelectedItem = dataTraking?.statusId;
  const shipmentIdSelectedItem = dataTraking?.shipmentId;
  const hasTemporaryStorage = !!warehouseTemporaryStorage?.estimatedEntryDate;
  const shipmentsAvailableToChangeStatus = isStockOrder
    ? listOfGroupedShipments?.filter(
        shipments => shipments?.statusId === stateOfTheSelectedItem && shipments?.shipmentId !== shipmentIdSelectedItem
      )
    : listOfGroupedShipments?.filter(
        shipments =>
          shipments?.statusId === stateOfTheSelectedItem &&
          shipments?.shipmentId !== shipmentIdSelectedItem &&
          !!shipments?.warehouseTemporaryStorage?.estimatedEntryDate === hasTemporaryStorage
      );

  const atWarehouseStatus = !isStockOrder && selectOption?.name === SHIPMENT_STATUS.WAREHOUSE_STOP;

  const statusMovementTitle = atWarehouseStatus
    ? 'De bodega a Proceso de entrega'
    : 'De Confirmado a Proceso de entrega';

  const modalContentTitle = isEdited ? 'Actualización del tracking' : statusMovementTitle;

  const closeModal = () => changeShowModal?.();

  const getMassUpdateBody = (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => {
    return shippingList.map(shipment => ({
      shipmentId: shipment?.shipmentId,
      trackingLink: data?.link || '',
      trackingNumber: data?.code || '',
      estimatedDeliveryDate: data?.date || '',
      newSerials: extractProductData(shipment?.products)
    }));
  };

  const updateDataStockOrder = async (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => {
    setLoadButton(true);
    try {
      await updateStockOrderShipmentStatus({
        stockOrderId: orderId || 0,
        stockShipmentStatusId: 2,
        deliveryProcessInfo: {
          trackingCode: data?.code || '',
          trackingLink: data?.link || '',
          estimatedDeliveryDate: data?.date || '',
          stockShipmentsIds: shippingList?.map(shipment => shipment?.shipmentId)
        }
      });
      await updateDataTraking?.();
      closeModal();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadButton(false);
    }
  };

  const updateDataMassive = async (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => {
    setLoadButton(true);
    try {
      const bodyData = getMassUpdateBody(data, shippingList);
      await massUpdateConfirmedToInProgress(bodyData);
      await updateDataTraking?.();
      closeModal();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadButton(false);
    }
  };

  const saveDataDeliveyProgress = async (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => {
    if (isStockOrder) return updateDataStockOrder(data, shippingList);
    return updateDataMassive(data, shippingList);
  };

  const saveEditDataStockOrder = async (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => {
    const stockShipmentUuids = shippingList
      .map(shipment => shipment.stockShipmentUuid)
      .filter((uuid): uuid is string => uuid !== undefined);
    if (stockShipmentUuids.length > 0) {
      await updateStockOrderShipmentTracking({
        stockShipmentsUuids: stockShipmentUuids,
        trackingCode: data?.code || '',
        trackingLink: data?.link || '',
        estimatedDeliveryDate: data?.date || ''
      });
    }
  };

  const saveEditDataMassive = async (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => {
    const shipmentIds = shippingList?.map(shipment => shipment?.shipmentId);
    const formatBody: IEditEstimatedDeliveryDate = {
      trackingLink: data?.link || '',
      trackingNumber: data?.code || '',
      estimatedDeliveryDate: data.date,
      shipmentIds: shipmentIds
    };
    await editEstimatedDeliveryDateInBulk(formatBody);
  };

  const saveEditData = async (data: IFormDeliveryProcess, shippingList: IShipmentData[]) => {
    setLoadButton(true);
    try {
      if (isStockOrder) {
        await saveEditDataStockOrder(data, shippingList);
      } else {
        await saveEditDataMassive(data, shippingList);
      }
      await updateDataTraking?.();
      closeModal();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadButton(false);
    }
  };

  return (
    <NudosGenericModal
      closeModalCallback={closeModal}
      clickOutsideCallback={closeModal}
      modalContent={
        <NudosModalContent
          customWidth="1048px"
          customHeight="744px"
          modalContentTitle={modalContentTitle}
          customHeaderClass="startDeliveryHeader"
          startHeader
          toCloseModal={closeModal}
          CustomModalComponent={
            <DeliveryProcessModalContent
              saveDataDeliveyProgress={saveDataDeliveyProgress}
              loadButton={loadButton}
              dataTraking={dataTraking}
              editData={saveEditData}
              isEdited={isEdited}
              organizationName={organizationName}
              receiverName={receiverName}
              orderId={orderId}
              flag={flag}
              shipmentsAvailableToChangeStatus={shipmentsAvailableToChangeStatus}
              generalData={generalData}
              atWarehouseStatus={atWarehouseStatus}
              references={references}
              isStockOrder={isStockOrder}
            />
          }
        />
      }
    />
  );
};

export default DeliveryProcessModal;
